import { useState, useCallback } from "react";
import { useTranslation } from "../i18n";
import PageShell from "../components/PageShell";
import MarkdownEditor from "../components/MarkdownEditor";
import DataTable from "../components/DataTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import ConfirmDialog from "../components/ConfirmDialog";
import toast from "react-hot-toast";

/* ------------------------------------------------------------------ */
/*  LocalStorage persistence helpers                                   */
/* ------------------------------------------------------------------ */

const CATEGORIES_KEY = "marketing_categories";
const TRIGGERS_KEY = "marketing_triggers";
const NOTIFICATIONS_KEY = "marketing_notifications";
const NOTIF_VERSION_KEY = "marketing_notif_version";
const NOTIF_VERSION = 2;

function lsLoad(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function useLocalStorage(key, fallback) {
  const [val, setVal] = useState(() => lsLoad(key, fallback));
  const setAndPersist = useCallback(
    (updater) => {
      setVal((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        localStorage.setItem(key, JSON.stringify(next));
        return next;
      });
    },
    [key],
  );
  return [val, setAndPersist];
}

function uid() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/* ------------------------------------------------------------------ */
/*  Default data                                                       */
/* ------------------------------------------------------------------ */

const DEFAULT_CATEGORIES = [
  {
    id: "cat_1",
    name: "Все клиенты",
    sql: "SELECT id, firstname, lastname, phone FROM customer WHERE archived = false",
  },
  {
    id: "cat_2",
    name: "Новые (до 30 дней)",
    sql: "SELECT id, firstname, lastname, phone FROM customer WHERE created_at >= NOW() - INTERVAL '30 days' AND archived = false",
  },
  {
    id: "cat_3",
    name: "VIP (100+ баллов)",
    sql: "SELECT id, firstname, lastname, phone FROM customer WHERE bonus_points >= 100 AND archived = false",
  },
];

const DEFAULT_TRIGGERS = [
  {
    id: "trg_1",
    name: "После покупки",
    sql: "SELECT DISTINCT c.id, c.firstname, c.lastname, c.phone FROM customer c JOIN transaction t ON t.customer_id = c.id WHERE t.created_at >= NOW() - INTERVAL '1 hour'",
    enabled: true,
  },
  {
    id: "trg_2",
    name: "День рождения",
    sql: "SELECT id, firstname, lastname, phone FROM customer WHERE EXTRACT(MONTH FROM birth_date) = EXTRACT(MONTH FROM CURRENT_DATE) AND EXTRACT(DAY FROM birth_date) = EXTRACT(DAY FROM CURRENT_DATE) AND archived = false",
    enabled: false,
  },
];

const MOCK_NOTIFICATIONS = [
  {
    id: "mock_1",
    title: "Специальное предложение",
    description: "Скидка 20% на все услуги в честь открытия",
    body: "## Специальное предложение\n\nУважаемый клиент! Мы рады предложить вам **скидку 20%** на все услуги нашего клуба.\n\n- Массаж\n- SPA-процедуры\n- Фитнес\n\n*Предложение действует до конца месяца.*",
    mode: "schedule",
    categoryId: "cat_1",
    categoryName: "Все клиенты",
    schedDate: "2026-06-15",
    schedTime: "10:00",
    repeat: "once",
    createdAt: "2026-06-01T08:00:00.000Z",
    status: "scheduled",
  },
  {
    id: "mock_2",
    title: "День рождения!",
    description: "Поздравление с днём рождения и бонус 50 минут",
    body: "## С днём рождения!\n\nПоздравляем вас с днём рождения! В честь праздника дарим **50 минут** на любой вид услуг.\n\n`Промокод: HAPPY50`\n\n[Записаться](https://example.com)",
    mode: "trigger",
    triggerId: "trg_2",
    triggerName: "День рождения",
    createdAt: "2026-06-01T07:00:00.000Z",
    status: "scheduled",
  },
  {
    id: "mock_3",
    title: "Напоминание о визите",
    description: "У вас запланирован визит на завтра",
    body: "Напоминаем о вашем визите завтра в **15:00**.\n\nЖдём вас!",
    mode: "schedule",
    categoryId: "cat_2",
    categoryName: "Новые (до 30 дней)",
    schedDate: "2026-06-02",
    schedTime: "09:00",
    repeat: "daily",
    createdAt: "2026-06-01T06:00:00.000Z",
    status: "scheduled",
  },
];

/* ------------------------------------------------------------------ */
/*  Repeat / status label maps                                         */
/* ------------------------------------------------------------------ */

function useRepeatLabels(t) {
  return {
    once: "—",
    daily: t("repeat.daily"),
    weekly: t("repeat.weekly"),
    monthly: t("repeat.monthly"),
  };
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

export default function MarketingDashboard() {
  const { t } = useTranslation();

  // Migrate old notification data on version bump
  if (localStorage.getItem(NOTIF_VERSION_KEY) !== String(NOTIF_VERSION)) {
    localStorage.removeItem(NOTIFICATIONS_KEY);
    localStorage.setItem(NOTIF_VERSION_KEY, String(NOTIF_VERSION));
  }

  /* ---- data ---- */
  const [categories, setCategories] = useLocalStorage(
    CATEGORIES_KEY,
    DEFAULT_CATEGORIES,
  );
  const [triggers, setTriggers] = useLocalStorage(
    TRIGGERS_KEY,
    DEFAULT_TRIGGERS,
  );
  const [notifications, setNotifications] = useLocalStorage(
    NOTIFICATIONS_KEY,
    MOCK_NOTIFICATIONS,
  );

  /* ---- create-notification form state ---- */
  const [formMode, setFormMode] = useState("schedule");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [body, setBody] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [triggerId, setTriggerId] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [schedDate, setSchedDate] = useState(today);
  const [schedTime, setSchedTime] = useState("12:00");
  const [repeat, setRepeat] = useState("once");

  /* ---- category/trigger dialog state ---- */
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catDraft, setCatDraft] = useState({ name: "", sql: "" });

  const [trgDialogOpen, setTrgDialogOpen] = useState(false);
  const [trgDraft, setTrgDraft] = useState({
    name: "",
    sql: "",
    enabled: true,
  });

  const [deleteTarget, setDeleteTarget] = useState(null);

  const repeatLabels = useRepeatLabels(t);

  /* ---- derived ---- */
  const catOptions = categories.map((c) => ({ value: c.id, name: c.name }));
  const trgOptions = triggers.map((t) => ({ value: t.id, name: t.name }));
  const selectedCat = categories.find((c) => c.id === categoryId);
  const selectedTrg = triggers.find((t) => t.id === triggerId);

  /* ---- helpers ---- */
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setBody("");
    setCategoryId("");
    setTriggerId("");
    setSchedDate(new Date().toISOString().split("T")[0]);
    setSchedTime("12:00");
    setRepeat("once");
    setFormMode("schedule");
  };

  const handleCreate = () => {
    if (!title.trim()) return void toast.error(t("toast.enterTitle"));
    if (!description.trim())
      return void toast.error(t("toast.enterDescription"));
    if (!body.trim()) return void toast.error(t("toast.enterBody"));

    if (formMode === "schedule") {
      if (!categoryId) return void toast.error(t("toast.selectCategory"));
      const cat = categories.find((c) => c.id === categoryId);
      setNotifications((prev) => [
        {
          id: uid(),
          title: title.trim(),
          description: description.trim(),
          body: body.trim(),
          mode: "schedule",
          categoryId,
          categoryName: cat?.name,
          schedDate,
          schedTime,
          repeat,
          createdAt: new Date().toISOString(),
          status: "scheduled",
        },
        ...prev,
      ]);
      toast.success(t("toast.notifScheduled"));
    } else {
      if (!triggerId) return void toast.error(t("toast.selectTrigger"));
      const trg = triggers.find((t) => t.id === triggerId);
      setNotifications((prev) => [
        {
          id: uid(),
          title: title.trim(),
          description: description.trim(),
          body: body.trim(),
          mode: "trigger",
          triggerId,
          triggerName: trg?.name,
          createdAt: new Date().toISOString(),
          status: "scheduled",
        },
        ...prev,
      ]);
      toast.success(t("toast.notifTriggerCreated"));
    }
    resetForm();
  };

  /* ---- category dialog ---- */
  const openNewCategory = () => {
    setCatDraft({ name: "", sql: "" });
    setCatDialogOpen(true);
  };
  const openEditCategory = () => {
    if (!selectedCat) return;
    setCatDraft({ ...selectedCat });
    setCatDialogOpen(true);
  };
  const saveCategory = () => {
    if (!catDraft.name.trim() || !catDraft.sql.trim()) {
      return void toast.error(t("toast.fillAllFields"));
    }
    if (catDraft.id) {
      setCategories((prev) =>
        prev.map((c) => (c.id === catDraft.id ? catDraft : c)),
      );
      toast.success(t("toast.categoryUpdated"));
    } else {
      const n = { ...catDraft, id: uid() };
      setCategories((prev) => [...prev, n]);
      setCategoryId(n.id);
      toast.success(t("toast.categoryCreated"));
    }
    setCatDialogOpen(false);
  };
  const deleteCategory = () => {
    if (!categoryId) return;
    setCategories((prev) => prev.filter((c) => c.id !== categoryId));
    setCategoryId("");
    toast.success(t("toast.categoryDeleted"));
  };

  /* ---- trigger dialog ---- */
  const openNewTrigger = () => {
    setTrgDraft({ name: "", sql: "", enabled: true });
    setTrgDialogOpen(true);
  };
  const openEditTrigger = () => {
    if (!selectedTrg) return;
    setTrgDraft({ ...selectedTrg });
    setTrgDialogOpen(true);
  };
  const saveTrigger = () => {
    if (!trgDraft.name.trim() || !trgDraft.sql.trim()) {
      return void toast.error(t("toast.fillAllFields"));
    }
    if (trgDraft.id) {
      setTriggers((prev) =>
        prev.map((t) => (t.id === trgDraft.id ? trgDraft : t)),
      );
      toast.success(t("toast.triggerUpdated"));
    } else {
      const n = { ...trgDraft, id: uid() };
      setTriggers((prev) => [...prev, n]);
      setTriggerId(n.id);
      toast.success(t("toast.triggerCreated"));
    }
    setTrgDialogOpen(false);
  };
  const deleteTrigger = () => {
    if (!triggerId) return;
    setTriggers((prev) => prev.filter((t) => t.id !== triggerId));
    setTriggerId("");
    toast.success(t("toast.triggerDeleted"));
  };

  /* ---- table columns ---- */
  const NOTIF_COLUMNS = [
    { header: t("marketing.title"), accessor: "title", type: "text" },
    {
      header: t("marketing.type"),
      accessor: "modeLabel",
      type: "custom",
      mapper: (v) =>
        v === "schedule" ? t("marketing.schedule") : t("marketing.trigger"),
    },
    { header: t("marketing.recipients"), accessor: "targetName", type: "text" },
    { header: t("marketing.date"), accessor: "schedDate", type: "text" },
    { header: t("marketing.time"), accessor: "schedTime", type: "text" },
    { header: t("marketing.repeat"), accessor: "repeatLabel", type: "text" },
    {
      header: t("marketing.status"),
      accessor: "status",
      type: "custom",
      mapper: (v) => (v && v !== "—" ? t(`status.${v}`) : "—"),
    },
  ];

  const tableRows = notifications.map((n) => ({
    id: n.id,
    title: n.title,
    modeLabel: n.mode,
    targetName:
      n.mode === "schedule" ? n.categoryName || "—" : n.triggerName || "—",
    schedDate: n.mode === "schedule" ? n.schedDate || "—" : "—",
    schedTime: n.mode === "schedule" ? n.schedTime || "—" : "—",
    repeatLabel: n.mode === "schedule" ? repeatLabels[n.repeat] || "—" : "—",
    status: n.status,
  }));

  return (
    <PageShell title={t("page.marketing")}>
      <div className="flex flex-col gap-4 h-full">
        {/* ---------- CREATE FORM ---------- */}
        <div className="w-full shrink-0">
          <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
            <h2 className="text-xl">{t("marketing.newNotification")}</h2>

            {/* Title */}
            <div>
              <Label>{t("marketing.title")}</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("marketing.titlePlaceholder")}
              />
            </div>

            {/* Description */}
            <div>
              <Label>{t("marketing.description")}</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="flex h-10 w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 min-h-[2.5rem] resize-y"
                placeholder={t("marketing.descPlaceholder")}
              />
            </div>

            {/* Body (Markdown) */}
            <MarkdownEditor
              label={t("marketing.bodyLabel")}
              value={body}
              onChange={setBody}
            />

            {/* Mode toggle */}
            <div className="flex gap-2">
              <Button
                variant={formMode === "schedule" ? "default" : "secondary"}
                onClick={() => setFormMode("schedule")}
                size="sm"
              >
                {t("marketing.schedule")}
              </Button>
              <Button
                variant={formMode === "trigger" ? "default" : "secondary"}
                onClick={() => setFormMode("trigger")}
                size="sm"
              >
                {t("marketing.trigger")}
              </Button>
            </div>

            {/* Schedule fields */}
            {formMode === "schedule" ? (
              <div className="space-y-3">
                {/* Category picker + actions */}
                <div>
                  <Label>{t("marketing.category")}</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select
                        value={categoryId}
                        onChange={(e) => setCategoryId(e.target.value)}
                      >
                        <option value="" disabled>
                          {t("common.selectPlaceholder", {
                            label: t("marketing.category").toLowerCase(),
                          })}
                        </option>
                        {catOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={openNewCategory}
                      title={t("marketing.newCategory")}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </Button>
                    {selectedCat && (
                      <>
                        <Button
                          variant="secondary"
                          size="icon"
                          onClick={openEditCategory}
                          title={t("marketing.editCategory")}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={deleteCategory}
                          title={t("common.delete")}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Date / Time / Repeat */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <Label>{t("marketing.date")}</Label>
                    <Input
                      type="date"
                      value={schedDate}
                      onChange={(e) => setSchedDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>{t("marketing.time")}</Label>
                    <Input
                      type="time"
                      value={schedTime}
                      onChange={(e) => setSchedTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>{t("marketing.repeat")}</Label>
                    <Select
                      value={repeat}
                      onChange={(e) => setRepeat(e.target.value)}
                    >
                      <option value="once">{t("repeat.once")}</option>
                      <option value="daily">{t("repeat.daily")}</option>
                      <option value="weekly">{t("repeat.weekly")}</option>
                      <option value="monthly">{t("repeat.monthly")}</option>
                    </Select>
                  </div>
                </div>
              </div>
            ) : (
              /* Trigger mode */
              <div>
                <Label>{t("marketing.trigger")}</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select
                      value={triggerId}
                      onChange={(e) => setTriggerId(e.target.value)}
                    >
                      <option value="" disabled>
                        {t("common.selectPlaceholder", {
                          label: t("marketing.trigger").toLowerCase(),
                        })}
                      </option>
                      {trgOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={openNewTrigger}
                    title={t("marketing.newTrigger")}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </Button>
                  {selectedTrg && (
                    <>
                      <Button
                        variant="secondary"
                        size="icon"
                        onClick={openEditTrigger}
                        title={t("marketing.editTrigger")}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={deleteTrigger}
                        title={t("common.delete")}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Create button */}
            <Button className="w-full" onClick={handleCreate}>
              {t("marketing.createNotif")}
            </Button>
          </div>
        </div>

        {/* ---------- NOTIFICATIONS TABLE ---------- */}
        <div className="w-full">
          <div className="bg-c-gray-600 rounded-lg p-4 flex flex-col">
            <h2 className="text-xl mb-4 shrink-0">
              {t("marketing.scheduledNotifs")}
              {notifications.length > 0 && (
                <span className="text-c-gray-400 text-base ml-2">
                  ({notifications.length})
                </span>
              )}
            </h2>
            <div className="overflow-auto">
              <DataTable
                columns={NOTIF_COLUMNS}
                data={tableRows}
                onRowClick={(row) => {
                  const notif = notifications.find((n) => n.id === row.id);
                  if (notif) setDeleteTarget(notif);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---- Category Dialog ---- */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {catDraft.id
                ? t("marketing.editCategory")
                : t("marketing.newCategory")}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveCategory();
            }}
            className="space-y-4"
          >
            <div>
              <Label>{t("marketing.name")}</Label>
              <Input
                value={catDraft.name}
                onChange={(e) =>
                  setCatDraft((p) => ({ ...p, name: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <Label>{t("marketing.sqlQuery")}</Label>
              <textarea
                value={catDraft.sql}
                onChange={(e) =>
                  setCatDraft((p) => ({ ...p, sql: e.target.value }))
                }
                rows={10}
                className="flex w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 font-mono resize-y"
                placeholder="SELECT id, firstname, lastname, phone FROM customer WHERE ..."
                required
              />
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setCatDialogOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("common.save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Trigger Dialog ---- */}
      <Dialog open={trgDialogOpen} onOpenChange={setTrgDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {trgDraft.id
                ? t("marketing.editTrigger")
                : t("marketing.newTrigger")}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveTrigger();
            }}
            className="space-y-4"
          >
            <div>
              <Label>{t("marketing.name")}</Label>
              <Input
                value={trgDraft.name}
                onChange={(e) =>
                  setTrgDraft((p) => ({ ...p, name: e.target.value }))
                }
                required
              />
            </div>
            <div>
              <Label>{t("marketing.sqlQuery")}</Label>
              <textarea
                value={trgDraft.sql}
                onChange={(e) =>
                  setTrgDraft((p) => ({ ...p, sql: e.target.value }))
                }
                rows={10}
                className="flex w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 font-mono resize-y"
                placeholder="SELECT c.id, c.firstname, c.lastname, c.phone FROM customer c JOIN ..."
                required
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={trgDraft.enabled ?? true}
                onChange={(e) =>
                  setTrgDraft((p) => ({ ...p, enabled: e.target.checked }))
                }
              />
              <span className="text-sm">{t("marketing.triggerEnabled")}</span>
            </label>
            <DialogFooter>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setTrgDialogOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("common.save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Delete confirmation ---- */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title={t("marketing.deleteNotif")}
        itemName={deleteTarget?.title}
        confirmText={t("common.delete")}
        onConfirm={() => {
          if (!deleteTarget) return;
          setNotifications((prev) =>
            prev.filter((n) => n.id !== deleteTarget.id),
          );
          setDeleteTarget(null);
          toast.success(t("toast.notifDeleted"));
        }}
      />
    </PageShell>
  );
}
